<template>
  <main dir="rtl" class="flex-1 pb-8 bg-gray-50 min-h-screen">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between mb-5 pt-2">
        <h1 class="text-3xl font-bold text-primary">سجل المحادثات اليومية للحلقات العامة</h1>
        <button class="px-3 py-2 bg-white rounded-xl border border-gray-200 text-gray-700 text-sm" @click="loadLogs">
          تحديث
        </button>
      </div>

      <div v-if="error" class="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {{ error }}
      </div>

      <div class="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div class="md:col-span-2">
            <label class="block mb-1 text-xs text-gray-500">بحث</label>
            <input
              v-model="filters.search"
              type="text"
              placeholder="ابحث عن حلقة أو معلم..."
              class="w-full px-3 py-2.5 border rounded-xl"
              @input="onSearchChange"
            />
          </div>
          <div>
            <label class="block mb-1 text-xs text-gray-500">الحلقة</label>
            <select v-model="filters.circleId" class="w-full px-3 py-2.5 border rounded-xl" @change="onCircleFilterChange">
              <option value="">كل الحلقات</option>
              <option v-for="circle in circles" :key="circle.id" :value="circle.id">
                {{ circle.title }}
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-1 text-xs text-gray-500">التاريخ</label>
            <input v-model="filters.date" type="date" class="w-full px-3 py-2.5 border rounded-xl" @change="onDateChange" />
          </div>
        </div>

        <div class="mt-3 flex flex-wrap gap-2">
          <button
            class="px-3 py-1.5 rounded-full border text-xs"
            :class="isTodaySelected ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200'"
            @click="selectToday">
            اليوم
          </button>
          <button
            v-for="dateItem in dateChips"
            :key="dateItem.date"
            class="px-3 py-1.5 rounded-full border text-xs"
            :class="filters.date === dateItem.date ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200'"
            @click="filters.date = dateItem.date; loadLogs()">
            {{ formatArabicDate(dateItem.date) }} ({{ dateItem.messagesCount }})
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div class="lg:col-span-1 bg-white rounded-xl border border-gray-200 p-3 max-h-[72vh] overflow-y-auto">
          <div class="text-xs text-gray-500 mb-2">المحادثات المتاحة</div>
          <div v-if="loading" class="text-center py-10 text-gray-500">جاري التحميل...</div>
          <div v-else-if="conversationCards.length === 0" class="text-center text-gray-400 py-10 text-sm">
            لا توجد محادثات في هذا التاريخ
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="conv in conversationCards"
              :key="conv.circleId"
              class="p-3 rounded-xl border cursor-pointer transition-all"
              :class="selectedCircleId === conv.circleId ? 'border-primary bg-primary-soft' : 'border-gray-200 hover:border-primary/30'"
              @click="selectConversation(conv.circleId)">
              <div class="flex items-start justify-between gap-2">
                <div>
                  <h3 class="font-bold text-sm text-gray-800">{{ conv.circleTitle }}</h3>
                  <p class="text-xs text-gray-500 mt-1">{{ conv.teacherName || '-' }}</p>
                </div>
                <span class="text-[10px] px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                  {{ conv.messagesCount }} رسالة
                </span>
              </div>
              <p class="text-xs text-gray-600 mt-3 truncate">{{ conv.lastMessagePreview }}</p>
              <p class="text-[10px] text-gray-400 mt-2">{{ conv.lastSentAt || '' }}</p>
            </div>
          </div>
        </div>

        <div class="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4 max-h-[72vh] overflow-y-auto">
          <template v-if="selectedConversation">
            <div class="border-b border-gray-100 pb-3 mb-3">
              <h2 class="text-lg font-bold text-gray-800">{{ selectedConversation.circleTitle }}</h2>
              <p class="text-xs text-gray-500 mt-1">المعلم: {{ selectedConversation.teacherName || '-' }}</p>
            </div>

            <div v-if="selectedMessages.length === 0" class="text-center text-gray-400 py-10 text-sm">
              لا توجد رسائل في هذه المحادثة
            </div>

            <div v-else class="space-y-3">
              <div
                v-for="msg in paginatedSelectedMessages"
                :key="msg.id"
                class="p-3 rounded-xl border border-gray-100 bg-gray-50">
                <div class="flex items-center justify-between gap-3">
                  <div class="text-sm">
                    <span class="font-semibold text-gray-800">{{ msg.senderName || '-' }}</span>
                    <span class="text-xs text-gray-500 mr-2">
                      ({{ normalizeRole(msg.senderRoleCode, msg.senderRole) }})
                    </span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-[11px] text-gray-400">{{ msg.sentAt || '-' }}</span>
                    <button
                      class="px-2 py-1 text-xs rounded border border-red-200 text-red-600 hover:bg-red-50"
                      @click="openDeleteModal(msg)">
                      حذف
                    </button>
                  </div>
                </div>
                <p class="text-sm text-gray-700 mt-2 leading-6 whitespace-pre-wrap">{{ msg.message || '-' }}</p>
              </div>

              <div v-if="hasMoreMessages" class="text-center pt-1">
                <button
                  class="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white hover:bg-gray-50"
                  @click="loadMoreMessages">
                  تحميل المزيد من الرسائل
                </button>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="h-full flex items-center justify-center text-gray-400 text-sm">
              اختر محادثة من القائمة لعرض الرسائل
            </div>
          </template>
        </div>
      </div>
    </div>

    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl w-full max-w-md p-5 border border-gray-100">
        <h3 class="text-lg font-bold text-gray-800 mb-2">تأكيد حذف الرسالة</h3>
        <p class="text-sm text-gray-600 leading-6">
          هل أنت متأكد من حذف هذه الرسالة؟ لا يمكن التراجع بعد الحذف.
        </p>

        <div class="mt-5 flex gap-2 justify-end">
          <button
            class="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
            @click="closeDeleteModal">
            إلغاء
          </button>
          <button
            class="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
            :disabled="deleting"
            @click="deleteSelectedMessage">
            {{ deleting ? 'جاري الحذف...' : 'حذف الرسالة' }}
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import {
  deletePublicCircleDailyChatMessage,
  getPublicCircleDailyChatDates,
  getPublicCircleDailyChatLogs,
  getPublicCircles,
} from '@/services/publicCircles';

const today = () => new Date().toISOString().slice(0, 10);

export default {
  name: 'PublicCircleDailyChatLogs',
  data() {
    return {
      loading: false,
      error: '',
      circles: [],
      items: [],
      dateChips: [],
      selectedCircleId: null,
      pageSize: 20,
      selectedMessagesPage: 1,
      showDeleteModal: false,
      selectedMessageForDelete: null,
      deleting: false,
      filters: {
        circleId: '',
        date: today(),
        search: '',
      },
    };
  },
  computed: {
    isTodaySelected() {
      return this.filters.date === today();
    },
    filteredItems() {
      const keyword = String(this.filters.search || '').trim().toLowerCase();
      if (!keyword) {
        return this.items;
      }
      return this.items.filter(item => {
        const circle = String(item.circleTitle || '').toLowerCase();
        const teacher = String(item.teacherName || '').toLowerCase();
        const sender = String(item.senderName || '').toLowerCase();
        const message = String(item.message || '').toLowerCase();
        return (
          circle.includes(keyword) ||
          teacher.includes(keyword) ||
          sender.includes(keyword) ||
          message.includes(keyword)
        );
      });
    },
    conversationCards() {
      const grouped = new Map();
      this.filteredItems.forEach(item => {
        if (!grouped.has(item.circleId)) {
          grouped.set(item.circleId, {
            circleId: item.circleId,
            circleTitle: item.circleTitle,
            teacherName: item.teacherName,
            messagesCount: 0,
            lastMessagePreview: '',
            lastSentAt: '',
          });
        }
        const row = grouped.get(item.circleId);
        row.messagesCount += 1;
        if (!row.lastMessagePreview) {
          row.lastMessagePreview = item.message || '';
          row.lastSentAt = item.sentAt || '';
        }
      });
      return Array.from(grouped.values());
    },
    selectedConversation() {
      if (!this.selectedCircleId) return null;
      return this.conversationCards.find(i => i.circleId === this.selectedCircleId) || null;
    },
    selectedMessages() {
      if (!this.selectedCircleId) return [];
      return this.filteredItems.filter(i => i.circleId === this.selectedCircleId);
    },
    paginatedSelectedMessages() {
      return this.selectedMessages.slice(0, this.selectedMessagesPage * this.pageSize);
    },
    hasMoreMessages() {
      return this.selectedMessages.length > this.paginatedSelectedMessages.length;
    },
  },
  async mounted() {
    await Promise.all([this.loadCircles(), this.loadDates(), this.loadLogs()]);
  },
  methods: {
    async loadCircles() {
      try {
        this.circles = await getPublicCircles();
      } catch (error) {
        this.circles = [];
      }
    },
    async loadLogs() {
      this.loading = true;
      this.error = '';
      try {
        const result = await getPublicCircleDailyChatLogs({
          circleId: this.filters.circleId || undefined,
          date: this.filters.date || undefined,
        });
        this.items = result?.items || [];
        if (!this.items.some(item => item.circleId === this.selectedCircleId)) {
          this.selectedCircleId = this.conversationCards[0]?.circleId || null;
        }
        this.selectedMessagesPage = 1;
      } catch (error) {
        this.error = error?.response?.data?.result?.mesAr || 'فشل تحميل سجل المحادثات';
      } finally {
        this.loading = false;
      }
    },
    async loadDates() {
      try {
        this.dateChips = await getPublicCircleDailyChatDates({
          circleId: this.filters.circleId || undefined,
        });
      } catch (error) {
        this.dateChips = [];
      }
    },
    onCircleFilterChange() {
      this.selectedCircleId = null;
      this.loadDates();
      this.loadLogs();
    },
    onDateChange() {
      this.selectedCircleId = null;
      this.selectedMessagesPage = 1;
      this.loadLogs();
    },
    selectToday() {
      this.filters.date = today();
      this.selectedCircleId = null;
      this.selectedMessagesPage = 1;
      this.loadLogs();
    },
    resetFilters() {
      this.filters = { circleId: '', date: today(), search: '' };
      this.selectedCircleId = null;
      this.selectedMessagesPage = 1;
      this.loadDates();
      this.loadLogs();
    },
    selectConversation(circleId) {
      this.selectedCircleId = circleId;
      this.selectedMessagesPage = 1;
    },
    onSearchChange() {
      this.selectedMessagesPage = 1;
    },
    loadMoreMessages() {
      this.selectedMessagesPage += 1;
    },
    openDeleteModal(item) {
      this.selectedMessageForDelete = item;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.selectedMessageForDelete = null;
    },
    async deleteSelectedMessage() {
      if (!this.selectedMessageForDelete?.id) return;
      try {
        this.deleting = true;
        await deletePublicCircleDailyChatMessage(this.selectedMessageForDelete.id);
        this.items = this.items.filter(i => i.id !== this.selectedMessageForDelete.id);
        this.closeDeleteModal();
        this.loadDates();
      } catch (error) {
        this.error = error?.response?.data?.result?.mesAr || 'تعذر حذف الرسالة';
      } finally {
        this.deleting = false;
      }
    },
    normalizeRole(roleCode, roleName) {
      const code = String(roleCode || '').toLowerCase();
      if (code === 'teacher') return 'معلم';
      if (code === 'student') return 'طالب';
      if (roleName) return roleName;
      return '-';
    },
    formatArabicDate(value) {
      try {
        return new Date(value).toLocaleDateString('ar-SA');
      } catch (error) {
        return value;
      }
    },
  },
};
</script>

<style scoped>
.text-primary {
  color: #c9a06c;
}
.bg-primary {
  background-color: #c9a06c;
}
.bg-primary-dark {
  background-color: #b8956a;
}
.bg-secondary {
  background-color: #d4a574;
}
.bg-primary-soft {
  background-color: rgba(201, 160, 108, 0.12);
}
</style>

